// Linus: Zugaengliches Disclosure fuer lange PDP-Beschreibungen mit Overflow-Erkennung und Scroll-Korrektur beim Einklappen.
class ProductDescriptionMore extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-product-description-content]');
    this.toggleButton = this.querySelector('[data-product-description-toggle]');

    if (!this.content || !this.toggleButton) return;

    this.readMoreLabel = this.dataset.readMoreLabel || 'Weiterlesen';
    this.readLessLabel = this.dataset.readLessLabel || 'Weniger anzeigen';
    this.collapsedLines = this.getCollapsedLines();
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.scheduleStateUpdate = this.scheduleStateUpdate.bind(this);

    this.ensureAccessibleDisclosure();
    this.toggleButton.addEventListener('click', this.handleToggleClick);
    this.scheduleStateUpdate();

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.scheduleStateUpdate);
      this.resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.toggleButton?.removeEventListener('click', this.handleToggleClick);
    this.resizeObserver?.disconnect();

    if (this.frameRequest) {
      window.cancelAnimationFrame(this.frameRequest);
    }
  }

  handleToggleClick() {
    this.setExpanded(!this.isExpanded(), true);
  }

  ensureAccessibleDisclosure() {
    if (!this.content.id) {
      this.content.id = `ProductDescriptionContent-${Math.random().toString(36).slice(2)}`;
    }

    this.toggleButton.setAttribute('aria-controls', this.content.id);
    this.toggleButton.setAttribute('aria-expanded', String(this.isExpanded()));
  }

  getCollapsedLines() {
    const lines = Number.parseInt(this.dataset.collapsedLines || '8', 10);

    return Number.isFinite(lines) && lines > 0 ? lines : 8;
  }

  getCollapsedHeight() {
    const styles = window.getComputedStyle(this.content);
    const fontSize = Number.parseFloat(styles.fontSize) || 16;
    const parsedLineHeight = Number.parseFloat(styles.lineHeight);
    const lineHeight = Number.isFinite(parsedLineHeight) ? parsedLineHeight : fontSize * 1.4;

    return Math.ceil(lineHeight * this.collapsedLines);
  }

  getScrollOffset() {
    const styles = window.getComputedStyle(document.body);
    const headerHeight = Number.parseFloat(styles.getPropertyValue('--header-group-height')) || 0;

    return headerHeight + 16;
  }

  isExpanded() {
    return this.classList.contains('is-expanded');
  }

  isCollapsible() {
    return this.classList.contains('is-collapsible');
  }

  setExpanded(isExpanded, shouldManageScroll = false) {
    if (!this.isCollapsible()) return;

    const wasExpanded = this.isExpanded();
    const shouldScrollAfterCollapse = shouldManageScroll && wasExpanded && !isExpanded;
    const scrollOffset = this.getScrollOffset();

    this.classList.toggle('is-expanded', isExpanded);
    this.toggleButton.textContent = isExpanded ? this.readLessLabel : this.readMoreLabel;
    this.toggleButton.setAttribute('aria-expanded', String(isExpanded));
    this.content.style.maxHeight = isExpanded ? `${this.content.scrollHeight}px` : `${this.getCollapsedHeight()}px`;

    if (shouldScrollAfterCollapse) {
      this.scrollCollapsedContentIntoView(scrollOffset);
    }
  }

  scrollCollapsedContentIntoView(scrollOffset) {
    window.requestAnimationFrame(() => {
      const top = this.getBoundingClientRect().top;

      if (top < scrollOffset) {
        window.scrollBy({
          top: top - scrollOffset,
          behavior: this.prefersReducedMotion.matches ? 'auto' : 'smooth',
        });
      }
    });
  }

  scheduleStateUpdate() {
    if (this.frameRequest) {
      window.cancelAnimationFrame(this.frameRequest);
    }

    this.frameRequest = window.requestAnimationFrame(() => {
      this.frameRequest = null;
      this.updateCollapsibleState();
    });
  }

  updateCollapsibleState() {
    const collapsedHeight = this.getCollapsedHeight();
    const fullHeight = this.content.scrollHeight;
    const canCollapse = fullHeight > collapsedHeight + 4;

    this.classList.toggle('is-collapsible', canCollapse);
    this.toggleButton.hidden = !canCollapse;

    if (!canCollapse) {
      this.classList.remove('is-expanded');
      this.toggleButton.textContent = this.readMoreLabel;
      this.toggleButton.setAttribute('aria-expanded', 'false');
      this.content.style.removeProperty('max-height');
      return;
    }

    this.content.style.maxHeight = this.isExpanded() ? `${fullHeight}px` : `${collapsedHeight}px`;
    this.toggleButton.textContent = this.isExpanded() ? this.readLessLabel : this.readMoreLabel;
    this.toggleButton.setAttribute('aria-expanded', String(this.isExpanded()));
  }
}

if (!customElements.get('product-description-more')) {
  customElements.define('product-description-more', ProductDescriptionMore);
}
